import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { formatPercent } from '@athena/i18n';
import { cn } from '../../lib/cn.js';
import { useLanguage } from '../../lib/i18n.js';
function clampPercent(value) {
    return Math.min(100, Math.max(0, value));
}
/**
 * The one track every bar in the product sits in (handoff, Atoms > Progress:
 * "14px track #E7E5E4, radius 999px").
 *
 * It exists as its own export because three components had grown three
 * versions of it — 14px fully rounded here, 12px with 2px/4px asymmetric
 * corners on the practice averages, 12px at 4px on the practice mix. Three
 * heights and four radii for one idea, none of them a decision anyone made
 * twice. The geometry now has one home.
 *
 * The role and the accessible name belong to the caller, not here: a bar
 * tracking a student through an attempt is a `progressbar` with a live value,
 * while a bar comparing finished scores is an `img` with a label, and the
 * track cannot know which it is inside.
 */
export function ProgressTrack({ children, className, ...props }) {
    return (_jsx("div", { className: cn('flex h-[14px] w-full overflow-hidden rounded-full bg-surface-tertiary', className), ...props, children: children }));
}
export function ProgressFill({ value, tone = 'progress', className }) {
    return (_jsx("div", { className: cn('h-full rounded-full transition-[width]', 
        // An inset ring rather than a border, so the edge paints inside the
        // fill without widening it: a border would make every bar report 2px
        // more progress than it has. At 0% the fill has no area and nothing
        // paints, which is right — there is no fill to outline.
        tone === 'progress' ? 'bg-accent inset-ring-1 inset-ring-accent-text' : 'bg-primary', 
        // Forced colours repaints fills to the page colour, which would leave
        // an empty track; the bar is data, so it keeps a colour there.
        'forced-colors:bg-[CanvasText] forced-colors:forced-color-adjust-none', className), style: { width: `${clampPercent(value)}%` } }));
}
export function ProgressLinear({ value, label, labelLevel, className }) {
    const percent = clampPercent(value);
    const Label = labelLevel ? `h${labelLevel}` : 'p';
    return (_jsxs("div", { className: cn('flex flex-col gap-2', className), children: [_jsx(ProgressTrack, { role: "progressbar", "aria-valuenow": Math.round(percent), "aria-valuemin": 0, "aria-valuemax": 100, "aria-label": label, children: _jsx(ProgressFill, { value: percent }) }), _jsx(Label, { className: "text-body-sm font-normal text-text-secondary", children: label })] }));
}
/** 120px conic ring, 88px white centre with the percentage in tabular
 * figures (README.md > Molecules > "Score indicator" gives the exact
 * 34px/700 size for that centre text). */
export function ProgressRing({ value, label, className }) {
    const language = useLanguage();
    const percent = clampPercent(value);
    return (_jsx("div", { role: "progressbar", "aria-valuenow": Math.round(percent), "aria-valuemin": 0, "aria-valuemax": 100, "aria-label": label, className: cn('relative inline-flex size-[120px] shrink-0 items-center justify-center rounded-full', className), style: {
            background: `conic-gradient(var(--color-accent) ${percent * 3.6}deg, var(--color-surface-tertiary) 0deg)`,
        }, children: _jsx("div", { className: "flex size-[88px] items-center justify-center rounded-full bg-surface text-[34px] font-bold tabular-nums text-text", children: formatPercent(percent, language) }) }));
}
//# sourceMappingURL=Progress.js.map